// Angular
import {
  afterNextRender,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
// PrimeNG
import { CarouselModule } from 'primeng/carousel';
import { SkeletonModule } from 'primeng/skeleton';
// Models
import { Slide } from '@core/models-v2/cms/slider-reponse.interface';
// Services
import { CmsService } from '@core/services-v2/cms.service';
import { GtmService } from '@core/utils-v2/gtm/gtm.service';
import { ScreenService } from '@shared/services/screen';

const NG_MODULES = [NgOptimizedImage];
const PRIME_MODULES = [CarouselModule, SkeletonModule];
declare let dataLayer: any;

@Component({
  selector: 'app-block-slideshow',
  templateUrl: './block-slideshow.component.html',
  styleUrls: ['./block-slideshow.component.scss'],
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES],
})
export class BlockSlideshowComponent implements OnInit {
  //#region Dependencies
  private readonly gtmService = inject(GtmService);
  private readonly cmsService = inject(CmsService);
  private readonly screenService = inject(ScreenService);
  private readonly platformId = inject(PLATFORM_ID);
  //#endregion

  //#region Signals
  slides = signal<Slide[]>([]);
  isCarouselReady = signal(false);
  isHydrated = signal(false);
  isLoading = signal(true);
  //#endregion

  //#region Computed signals derivados
  isMobile = this.screenService.isMobile;
  hasSlides = computed(() => this.slides().length > 0);
  firstSlide = computed(() => this.slides()[0] || null);
  shouldShowCarousel = computed(
    () => this.hasSlides() && this.isCarouselReady() && this.isHydrated()
  );
  shouldShowStatic = computed(
    () => this.hasSlides() && !this.shouldShowCarousel()
  );
  //#endregion

  constructor() {
    // ✅ afterNextRender: Ejecuta después del primer render completo
    afterNextRender(() => {
      this.isHydrated.set(true);
    });

    // ✅ Effect: Reacciona a cambios en slides e hydration
    effect(() => {
      const slides = this.slides();
      const hydrated = this.isHydrated();

      if (slides.length && hydrated) {
        this.initializeCarousel();
      }
    });

    // ✅ Effect: Para preload cuando tenemos slides
    effect(() => {
      const firstSlide = this.firstSlide();
      const hydrated = this.isHydrated();

      if (firstSlide && hydrated) {
        this.preloadCriticalImage();
      }
    });
  }

  ngOnInit(): void {
    this.loadSlides();
  }

  private loadSlides(): void {
    this.cmsService.getSlides().subscribe({
      next: (res) => {
        this.slides.set(res); // ✅ Update signal
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading slides:', err);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Inicializa el carrusel una vez que los slides están listos.
   * @returns void
   */
  private initializeCarousel(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // ✅ Use requestIdleCallback para trabajo no crítico
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          this.isCarouselReady.set(true);
        },
        { timeout: 1000 }
      );
    } else {
      // Fallback para navegadores sin requestIdleCallback
      setTimeout(() => {
        this.isCarouselReady.set(true);
      }, 100);
    }
  }

  /**
   * Preload de la imagen crítica.
   */
  private preloadCriticalImage(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const firstSlide = this.firstSlide();
    if (!firstSlide) return;

    const imageUrl = this.getCurrentSlideImage(firstSlide);

    const existingPreload = document.querySelector(`link[href="${imageUrl}"]`);
    if (existingPreload) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageUrl;
    link.imageSizes = '100vw';

    // ✅ Usar setAttribute en lugar de property
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);

    this.preloadSecondaryImage();
    this.updateMetaTags(firstSlide);
  }

  /**
   * Actualiza las meta etiquetas para SEO y redes sociales.
   * @param slide - Slide actual
   */
  private updateMetaTags(slide: Slide): void {
    const imageUrl = this.getCurrentSlideImage(slide);

    const metaUpdates = [
      { property: 'og:title', content: `${slide.alt} - Implementos` },
      {
        property: 'og:description',
        content: 'Repuestos para Camiones, Buses y Remolque',
      },
      { property: 'og:image', content: imageUrl },
      { property: 'og:url', content: slide.url },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: imageUrl },
    ];

    metaUpdates.forEach(({ property, name, content }) => {
      this.updateOrCreateMetaTag(property, name, content);
    });
  }

  private updateOrCreateMetaTag(
    property?: string,
    name?: string,
    content?: string
  ): void {
    const selector = property
      ? `meta[property="${property}"]`
      : `meta[name="${name}"]`;
    let meta = document.querySelector(selector) as HTMLMetaElement;

    if (!meta) {
      meta = document.createElement('meta');
      if (property) meta.setAttribute('property', property);
      if (name) meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    if (content) meta.setAttribute('content', content);
  }

  private preloadSecondaryImage(): void {
    const slides = this.slides();
    if (slides.length < 2) return;

    const secondSlide = slides[1];
    const imageUrl = this.getCurrentSlideImage(secondSlide);

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageUrl;
    link.imageSizes = '100vw';

    // ✅ Usar setAttribute en lugar de property
    link.setAttribute('fetchpriority', 'low');
    document.head.appendChild(link);
  }

  /**
   * Obtener la imagen actual del slide según el dispositivo.
   * @param slide - Slide actual
   * @returns La URL de la imagen correspondiente al dispositivo.
   */
  getCurrentSlideImage(slide: Slide): string {
    return this.isMobile() ? slide.imageMobile : slide.imageClassic;
  }

  /**
   * Generar structured data para SEO.
   */
  getStructuredData(): string {
    const slides = this.slides();
    if (!slides.length) return '';

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Productos destacados - Implementos',
      description: 'Repuestos para Camiones, Buses y Remolque',
      url: 'https://implementos.cl',
      numberOfItems: slides.length,
      itemListElement: slides.map((slide, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: slide.alt,
          description: slide.title || slide.alt,
          url: slide.url,
          image: {
            '@type': 'ImageObject',
            url: this.getCurrentSlideImage(slide),
            width: this.isMobile() ? '800' : '1920',
            height: this.isMobile() ? '800' : '576',
          },
        },
      })),
    };

    return JSON.stringify(structuredData);
  }

  /**
   * Handler para clicks en banners.
   */
  onClickBanner(event: Event, index: number): void {
    event.stopPropagation();
    this.gtmService.click({
      dataLayer,
      textId: String(index + 1),
      prefix: 'banner',
    });
  }
}
